import { useCallback, useMemo, useState } from 'react';
import { api } from '../../api/client.js';
import { normalizeVitrineProductIds } from '../../theme/storeHomeLayout.js';

/** @param {string} s */
function parseBody(s) {
  try {
    const o = JSON.parse(s.trim() === '' ? '{}' : s);
    if (!o || typeof o !== 'object' || Array.isArray(o)) return { ok: false, error: 'O JSON tem de ser um objecto.', body: null };
    return { ok: true, error: '', body: /** @type {Record<string, unknown>} */ (o) };
  } catch {
    return { ok: false, error: 'JSON inválido — confira vírgulas e aspas.', body: null };
  }
}

/** @param {Record<string, unknown>} body */
function stringifyBody(body) {
  return JSON.stringify(body, null, 2);
}

/**
 * Editor visual da vitrine (hero + activação por item) — sincroniza com o textarea JSON.
 * @param {{ tenantId: number | null; json: string; onJsonChange: (s: string) => void; disabled?: boolean }} props
 */
export function StoreVitrineLayoutForm({ tenantId, json, onJsonChange, disabled = false }) {
  const parsed = useMemo(() => parseBody(json), [json]);
  const [uploadBusy, setUploadBusy] = useState(false);
  const [uploadErr, setUploadErr] = useState('');

  const commit = useCallback(
    (body) => {
      onJsonChange(stringifyBody(body));
    },
    [onJsonChange],
  );

  const body = parsed.ok && parsed.body ? parsed.body : null;
  const hero = body && Array.isArray(body.hero) ? body.hero : [];
  const sections = body && Array.isArray(body.sections) ? body.sections : [];

  async function uploadHeroImage(slideIndex, file) {
    if (!tenantId || !file) return;
    setUploadErr('');
    setUploadBusy(true);
    try {
      const fd = new FormData();
      fd.append('imagem', file);
      const r = await api(`/tenants/${tenantId}/vitrine-hero-upload`, { method: 'POST', body: fd });
      const url = r?.url;
      if (typeof url !== 'string' || !url.trim()) {
        setUploadErr('Resposta do servidor sem URL da imagem.');
        return;
      }
      if (!body) return;
      const next = JSON.parse(JSON.stringify(body));
      const slides = Array.isArray(next.hero) ? [...next.hero] : [];
      while (slides.length <= slideIndex) slides.push({});
      const cur = slides[slideIndex] && typeof slides[slideIndex] === 'object' ? { ...slides[slideIndex] } : {};
      cur.image_url = url.trim();
      slides[slideIndex] = cur;
      next.hero = slides;
      commit(next);
    } catch (e) {
      setUploadErr(e?.message || 'Falha no envio da imagem.');
    } finally {
      setUploadBusy(false);
    }
  }

  function patchHero(index, patch) {
    if (!body) return;
    const next = JSON.parse(JSON.stringify(body));
    const slides = Array.isArray(next.hero) ? [...next.hero] : [];
    while (slides.length <= index) slides.push({});
    const cur = slides[index] && typeof slides[index] === 'object' ? { ...slides[index] } : {};
    slides[index] = { ...cur, ...patch };
    next.hero = slides;
    commit(next);
  }

  function patchSection(index, patch) {
    if (!body) return;
    const next = JSON.parse(JSON.stringify(body));
    const secs = Array.isArray(next.sections) ? [...next.sections] : [];
    if (!secs[index] || typeof secs[index] !== 'object') return;
    secs[index] = { ...secs[index], ...patch };
    next.sections = secs;
    commit(next);
  }

  function patchSectionItem(secIndex, itemIndex, patch) {
    if (!body) return;
    const next = JSON.parse(JSON.stringify(body));
    const secs = Array.isArray(next.sections) ? [...next.sections] : [];
    const sec = secs[secIndex];
    if (!sec || typeof sec !== 'object') return;
    const items = Array.isArray(sec.items) ? [...sec.items] : [];
    while (items.length <= itemIndex) items.push({});
    const cur = items[itemIndex] && typeof items[itemIndex] === 'object' ? { ...items[itemIndex] } : {};
    items[itemIndex] = { ...cur, ...patch };
    secs[secIndex] = { ...sec, items };
    next.sections = secs;
    commit(next);
  }

  function patchProductRowIds(secIndex, entries) {
    if (!body) return;
    const next = JSON.parse(JSON.stringify(body));
    const secs = Array.isArray(next.sections) ? [...next.sections] : [];
    const sec = secs[secIndex];
    if (!sec || typeof sec !== 'object') return;
    secs[secIndex] = { ...sec, product_ids: entries };
    next.sections = secs;
    commit(next);
  }

  function patchCard(secIndex, cardIndex, patch) {
    if (!body) return;
    const next = JSON.parse(JSON.stringify(body));
    const secs = Array.isArray(next.sections) ? [...next.sections] : [];
    const sec = secs[secIndex];
    if (!sec || typeof sec !== 'object') return;
    const cards = Array.isArray(sec.cards) ? [...sec.cards] : [];
    while (cards.length <= cardIndex) cards.push({});
    const cur = cards[cardIndex] && typeof cards[cardIndex] === 'object' ? { ...cards[cardIndex] } : {};
    cards[cardIndex] = { ...cur, ...patch };
    secs[secIndex] = { ...sec, cards };
    next.sections = secs;
    commit(next);
  }

  function patchCardProductIds(secIndex, cardIndex, entries) {
    if (!body) return;
    const next = JSON.parse(JSON.stringify(body));
    const secs = Array.isArray(next.sections) ? [...next.sections] : [];
    const sec = secs[secIndex];
    if (!sec || typeof sec !== 'object') return;
    const cards = Array.isArray(sec.cards) ? [...sec.cards] : [];
    while (cards.length <= cardIndex) cards.push({});
    const cur = cards[cardIndex] && typeof cards[cardIndex] === 'object' ? { ...cards[cardIndex] } : {};
    cards[cardIndex] = { ...cur, product_ids: entries };
    secs[secIndex] = { ...sec, cards };
    next.sections = secs;
    commit(next);
  }

  if (!parsed.ok || !body) {
    return (
      <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs font-medium text-amber-950 dark:text-amber-100">
        {parsed.error || 'JSON inválido — corrija o texto abaixo para usar o editor visual.'}
      </div>
    );
  }

  return (
    <div className="space-y-4 text-xs text-ink/80 dark:text-mist/80">
      {uploadErr ? (
        <p className="rounded-lg border border-red-500/35 bg-red-500/10 px-2 py-1.5 text-[11px] font-medium text-red-900 dark:text-red-100">
          {uploadErr}
        </p>
      ) : null}

      <div className="rounded-xl border border-ink/10 bg-ink/[0.03] p-3 dark:border-white/10 dark:bg-white/[0.04]">
        <p className="text-[11px] font-semibold text-ink dark:text-mist">Carrossel (hero)</p>
        <p className="mt-0.5 text-[10px] leading-snug text-ink/60 dark:text-frost-muted">
          Desmarque «Activar» para ocultar um slide sem apagar. Envie imagens — a URL é preenchida automaticamente.
        </p>
        <div className="mt-3 space-y-3">
          {hero.length === 0 ? <p className="text-[11px] text-ink/55">Nenhum slide em `hero`.</p> : null}
          {hero.map((slide, i) => {
            const s = slide && typeof slide === 'object' ? slide : {};
            const en = s.enabled !== false;
            const img = typeof s.image_url === 'string' ? s.image_url : '';
            const title = typeof s.title === 'string' ? s.title : '';
            const subtitle = typeof s.subtitle === 'string' ? s.subtitle : '';
            const href = typeof s.href === 'string' ? s.href : '';
            const video = typeof s.video_url === 'string' ? s.video_url : '';
            return (
              <div
                key={i}
                className="rounded-lg border border-ink/10 bg-white p-2 dark:border-white/10 dark:bg-[#252f2a]"
              >
                <label className="flex items-center gap-2 font-semibold text-ink dark:text-mist">
                  <input
                    type="checkbox"
                    checked={en}
                    disabled={disabled || uploadBusy}
                    onChange={(e) => patchHero(i, { enabled: e.target.checked })}
                    className="h-3.5 w-3.5 rounded border-ink/30 text-accent focus:ring-accent dark:border-white/25"
                  />
                  Activar slide {i + 1}
                </label>
                <div className="mt-2 grid gap-2 sm:grid-cols-2">
                  <label className="block font-medium">
                    URL da imagem
                    <input
                      className="mt-0.5 w-full rounded-lg border border-ink/10 bg-white px-2 py-1 font-mono text-[10px] dark:border-white/10 dark:bg-ink"
                      value={img}
                      disabled={disabled || uploadBusy}
                      onChange={(e) => patchHero(i, { image_url: e.target.value })}
                    />
                  </label>
                  <label className="block font-medium">
                    Enviar ficheiro
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      disabled={disabled || uploadBusy || !tenantId}
                      className="mt-0.5 block w-full text-[10px]"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        e.target.value = '';
                        if (f) uploadHeroImage(i, f);
                      }}
                    />
                  </label>
                  <label className="block font-medium sm:col-span-2">
                    Título
                    <input
                      className="mt-0.5 w-full rounded-lg border border-ink/10 bg-white px-2 py-1 dark:border-white/10 dark:bg-ink"
                      value={title}
                      disabled={disabled || uploadBusy}
                      onChange={(e) => patchHero(i, { title: e.target.value })}
                    />
                  </label>
                  <label className="block font-medium sm:col-span-2">
                    Subtítulo
                    <input
                      className="mt-0.5 w-full rounded-lg border border-ink/10 bg-white px-2 py-1 dark:border-white/10 dark:bg-ink"
                      value={subtitle}
                      disabled={disabled || uploadBusy}
                      onChange={(e) => patchHero(i, { subtitle: e.target.value })}
                    />
                  </label>
                  <label className="block font-medium sm:col-span-2">
                    Link (href)
                    <input
                      className="mt-0.5 w-full rounded-lg border border-ink/10 bg-white px-2 py-1 font-mono text-[10px] dark:border-white/10 dark:bg-ink"
                      value={href}
                      disabled={disabled || uploadBusy}
                      onChange={(e) => patchHero(i, { href: e.target.value })}
                    />
                  </label>
                  <label className="block font-medium sm:col-span-2">
                    Vídeo (URL opcional)
                    <input
                      className="mt-0.5 w-full rounded-lg border border-ink/10 bg-white px-2 py-1 font-mono text-[10px] dark:border-white/10 dark:bg-ink"
                      value={video}
                      disabled={disabled || uploadBusy}
                      onChange={(e) => patchHero(i, { video_url: e.target.value })}
                    />
                  </label>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="rounded-xl border border-ink/10 bg-ink/[0.03] p-3 dark:border-white/10 dark:bg-white/[0.04]">
        <p className="text-[11px] font-semibold text-ink dark:text-mist">Secções</p>
        <div className="mt-2 space-y-3">
          {sections.length === 0 ? <p className="text-[11px] text-ink/55">Nenhuma secção.</p> : null}
          {sections.map((sec, si) => {
            if (!sec || typeof sec !== 'object') return null;
            const type = String(sec.type || '').trim() || '(sem type)';
            const en = sec.enabled !== false;
            return (
              <div
                key={si}
                className="rounded-lg border border-ink/10 bg-white p-2 dark:border-white/10 dark:bg-[#252f2a]"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded bg-ink/10 px-1.5 py-0.5 font-mono text-[10px] text-ink dark:bg-white/10 dark:text-mist">
                    {type}
                  </span>
                  <label className="flex items-center gap-2 font-semibold text-ink dark:text-mist">
                    <input
                      type="checkbox"
                      checked={en}
                      disabled={disabled || uploadBusy}
                      onChange={(e) => patchSection(si, { enabled: e.target.checked })}
                      className="h-3.5 w-3.5 rounded border-ink/30 text-accent focus:ring-accent dark:border-white/25"
                    />
                    Activar secção
                  </label>
                </div>

                {type === 'category_strip' && Array.isArray(sec.items) ? (
                  <div className="mt-2 space-y-2 border-t border-ink/10 pt-2 dark:border-white/10">
                    <p className="text-[10px] font-semibold text-ink/70 dark:text-mist/70">Itens do carrossel de categorias</p>
                    {sec.items.map((it, ii) => {
                      if (!it || typeof it !== 'object') return null;
                      const ien = it.enabled !== false;
                      return (
                        <label key={ii} className="flex items-center gap-2 text-[11px]">
                          <input
                            type="checkbox"
                            checked={ien}
                            disabled={disabled || uploadBusy}
                            onChange={(e) => patchSectionItem(si, ii, { enabled: e.target.checked })}
                            className="h-3 w-3 rounded border-ink/30 text-accent dark:border-white/25"
                          />
                          <span className="truncate font-medium">{String(it.label || `Item ${ii + 1}`)}</span>
                        </label>
                      );
                    })}
                  </div>
                ) : null}

                {type === 'product_row' ? (
                  <div className="mt-2 space-y-2 border-t border-ink/10 pt-2 dark:border-white/10">
                    <p className="text-[10px] font-semibold text-ink/70 dark:text-mist/70">
                      Produtos (ID + mostrar na vitrine). Marque para incluir na fila da loja.
                    </p>
                    {normalizeVitrineProductIds(sec.product_ids).map((row, ri) => (
                      <div key={ri} className="flex flex-wrap items-center gap-2">
                        <label className="flex items-center gap-1.5">
                          <input
                            type="checkbox"
                            checked={row.enabled}
                            disabled={disabled || uploadBusy}
                            onChange={(e) => {
                              const list = normalizeVitrineProductIds(sec.product_ids);
                              list[ri] = { ...list[ri], enabled: e.target.checked };
                              patchProductRowIds(si, list);
                            }}
                            className="h-3 w-3 rounded border-ink/30 text-accent dark:border-white/25"
                          />
                        </label>
                        <input
                          type="number"
                          min={1}
                          className="w-24 rounded border border-ink/10 bg-white px-2 py-0.5 font-mono text-[11px] dark:border-white/10 dark:bg-ink"
                          value={row.id || ''}
                          disabled={disabled || uploadBusy}
                          onChange={(e) => {
                            const n = Math.max(1, Math.floor(Number(e.target.value) || 1));
                            const list = normalizeVitrineProductIds(sec.product_ids);
                            list[ri] = { ...list[ri], id: n };
                            patchProductRowIds(si, list);
                          }}
                        />
                        <button
                          type="button"
                          disabled={disabled || uploadBusy}
                          className="text-[10px] font-semibold text-red-700 underline-offset-2 hover:underline dark:text-red-300"
                          onClick={() => {
                            const list = normalizeVitrineProductIds(sec.product_ids).filter((_, j) => j !== ri);
                            patchProductRowIds(si, list);
                          }}
                        >
                          Remover
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      disabled={disabled || uploadBusy}
                      className="rounded border border-ink/15 px-2 py-1 text-[10px] font-semibold dark:border-white/20"
                      onClick={() => {
                        const list = [...normalizeVitrineProductIds(sec.product_ids), { id: 1, enabled: true }];
                        patchProductRowIds(si, list);
                      }}
                    >
                      + Adicionar ID de produto
                    </button>
                  </div>
                ) : null}

                {type === 'produtos_destaque' ? (
                  <div className="mt-2 grid gap-2 border-t border-ink/10 pt-2 sm:grid-cols-2 dark:border-white/10">
                    <label className="block font-medium">
                      Título
                      <input
                        className="mt-0.5 w-full rounded-lg border border-ink/10 bg-white px-2 py-1 dark:border-white/10 dark:bg-ink"
                        value={typeof sec.title === 'string' ? sec.title : ''}
                        disabled={disabled || uploadBusy}
                        onChange={(e) => patchSection(si, { title: e.target.value })}
                      />
                    </label>
                    <label className="block font-medium">
                      Limite (máx. 48)
                      <input
                        type="number"
                        min={1}
                        max={48}
                        className="mt-0.5 w-full rounded-lg border border-ink/10 bg-white px-2 py-1 dark:border-white/10 dark:bg-ink"
                        value={sec.limit != null ? String(sec.limit) : '12'}
                        disabled={disabled || uploadBusy}
                        onChange={(e) => {
                          const n = Math.min(48, Math.max(1, Math.floor(Number(e.target.value) || 12)));
                          patchSection(si, { limit: n });
                        }}
                      />
                    </label>
                    <p className="text-[10px] leading-snug text-ink/55 sm:col-span-2 dark:text-frost-muted">
                      Lista produtos com «em destaque» activo no catálogo (ERP ou painel Produtos). Só aparecem os que
                      já estão carregados na página.
                    </p>
                  </div>
                ) : null}

                {type === 'card_grid' && Array.isArray(sec.cards) ? (
                  <div className="mt-2 space-y-2 border-t border-ink/10 pt-2 dark:border-white/10">
                    {sec.cards.map((card, ci) => {
                      if (!card || typeof card !== 'object') return null;
                      const variant = String(card.variant || 'quad');
                      const cen = card.enabled !== false;
                      return (
                        <div key={ci} className="rounded border border-ink/10 p-2 dark:border-white/10">
                          <label className="flex items-center gap-2 text-[11px] font-semibold">
                            <input
                              type="checkbox"
                              checked={cen}
                              disabled={disabled || uploadBusy}
                              onChange={(e) => patchCard(si, ci, { enabled: e.target.checked })}
                              className="h-3 w-3 rounded border-ink/30 text-accent dark:border-white/25"
                            />
                            Cartão {ci + 1} ({variant})
                          </label>
                          {variant === 'quad' ? (
                            <div className="mt-2 space-y-1.5">
                              {normalizeVitrineProductIds(card.product_ids).map((row, ri) => (
                                <div key={ri} className="flex flex-wrap items-center gap-2">
                                  <input
                                    type="checkbox"
                                    checked={row.enabled}
                                    disabled={disabled || uploadBusy}
                                    onChange={(e) => {
                                      const list = normalizeVitrineProductIds(card.product_ids);
                                      list[ri] = { ...list[ri], enabled: e.target.checked };
                                      patchCardProductIds(si, ci, list);
                                    }}
                                    className="h-3 w-3 rounded border-ink/30 text-accent dark:border-white/25"
                                  />
                                  <input
                                    type="number"
                                    min={1}
                                    className="w-24 rounded border border-ink/10 bg-white px-2 py-0.5 font-mono text-[11px] dark:border-white/10 dark:bg-ink"
                                    value={row.id || ''}
                                    disabled={disabled || uploadBusy}
                                    onChange={(e) => {
                                      const n = Math.max(1, Math.floor(Number(e.target.value) || 1));
                                      const list = normalizeVitrineProductIds(card.product_ids);
                                      list[ri] = { ...list[ri], id: n };
                                      patchCardProductIds(si, ci, list);
                                    }}
                                  />
                                  <button
                                    type="button"
                                    disabled={disabled || uploadBusy}
                                    className="text-[10px] font-semibold text-red-700 underline-offset-2 hover:underline dark:text-red-300"
                                    onClick={() => {
                                      const list = normalizeVitrineProductIds(card.product_ids).filter((_, j) => j !== ri);
                                      patchCardProductIds(si, ci, list);
                                    }}
                                  >
                                    Remover
                                  </button>
                                </div>
                              ))}
                              <button
                                type="button"
                                disabled={disabled || uploadBusy}
                                className="mt-1 rounded border border-ink/15 px-2 py-0.5 text-[10px] font-semibold dark:border-white/20"
                                onClick={() => {
                                  const list = [...normalizeVitrineProductIds(card.product_ids), { id: 1, enabled: true }];
                                  patchCardProductIds(si, ci, list);
                                }}
                              >
                                + ID na grelha
                              </button>
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
